import type { GADataProps } from "@elements/GAData/GAData";
import type { MetaTagsProps } from "@elements/MetaTags/MetaTags";
import { LatestVideoResponse } from "@helper/transformer/useBrightcoveResponse";
import { ProcessedArticleData } from "@helper/transformer/useOsResponse";
import { formatDate } from "@helper/useDateFormat";
import { ProcessedArticleBody } from "@typings/Opensearch";
import type { PageMetaDataValues } from "@typings/PageMetaData";
import {
  AdsCardProps,
  FinishCardProps,
  QuizCardProps,
} from "@typings/QuickNews";

import { getAuthorsName, getTagsName } from "../Article/ArticleMetaData";

type CardsMetaDataProps = {
  card:
    | ProcessedArticleData
    | QuizCardProps
    | AdsCardProps
    | FinishCardProps
    | LatestVideoResponse;
  activeTab: string;
  onBoarded: boolean;
  pagination: number;
};

export default function QuickNewsMetaData({
  card,
  activeTab,
  onBoarded,
  pagination,
}: CardsMetaDataProps): PageMetaDataValues {
  const isArticle =
    !("adType" in card || "quizzes" in card) && "id" in card && "title" in card;

  const cardTitle =
    "title" in card ? card.title : "name" in card ? card.name : "";
  const cardId = "id" in card ? card.id : "";
  const defaultMetaTitle =
    "முன்னோட்டம்: அண்மைச் செய்திகள் - Quick News & Shorts by Tamil Murasu";
  const defaultMetaDesc =
    "Catch latest shorts & easy-to-read news. Quick News (முன்னோட்டம்) has அரசியல், சமூகம், பிரபலமான தலைப்புச் செய்திகள், காணொளிகள்; சுருக்கமாக, வேகமாக.";
  const defaultMetaSlug = "quick-news/";

  /* -----------------------------
   * GA DATA
   * ----------------------------- */
  const gaData: GADataProps = onBoarded
    ? {
        level2: "quick-news",
        title: cardTitle,
        contentcat: 1,
        articleid: isArticle ? cardId : "",
        cue_articleid: isArticle ? cardId : "",
        contenttype: "1",
        pagination: pagination + 1,
        author: "authors" in card ? getAuthorsName(card.authors || []) : "",
        pubdate:
          "datetime" in card
            ? formatDate(card.datetime, "yyyy-MM-dd HH:mm:ss")
            : "",
        content_publication_date_utc: "datetime" in card ? card.datetime : "",
        content_update_date_utc: "dateEdited" in card ? card.dateEdited : "",
        keyword: "tags" in card ? getTagsName(card.tags || []) : "",
      }
    : {
        level2: "quick-news",
        title: "quick_news_index",
        contentcat: 1,
        contenttype: undefined,
        pagination: 0,
      };

  /* -----------------------------
   * META TITLE
   * ----------------------------- */
  const metaTitle = onBoarded ? cardTitle : defaultMetaTitle;

  /* -----------------------------
   * META DESCRIPTION
   * ----------------------------- */
  const metaDesc = onBoarded
    ? getMetaDescription(card, activeTab) || ""
    : defaultMetaDesc;

  /* -----------------------------
   * META SLUG
   * ----------------------------- */
  const metaSlug = onBoarded
    ? `quick-news/${activeTab === "Videos" ? `video/${cardId}` : cardId}`
    : defaultMetaSlug;

  /* -----------------------------
   * META Image
   * ----------------------------- */
  const metaImage =
    activeTab === "MyFeed" && "imageField" in card
      ? (card.imageField?.url ?? "")
      : activeTab === "Videos" && "thumbnail" in card
        ? card.thumbnail
        : "";

  const metaTags: MetaTagsProps = {
    title: metaTitle,
    description: metaDesc,
    slug: metaSlug,
    image: metaImage,
  };

  return {
    gaData,
    metaTags,
  };
}

/* -----------------------------------------------------
 * Helper: Description builder
 * ----------------------------------------------------- */
function getMetaDescription(
  card:
    | ProcessedArticleData
    | QuizCardProps
    | AdsCardProps
    | FinishCardProps
    | LatestVideoResponse,
  activeTab: string,
): string {
  if ("articleBody" in card) {
    const text = getFirstTwoLines(card.articleBody);
    if (text) return text;
  }

  if ("description" in card && activeTab === "Videos") {
    return card.description || "";
  }

  return "";
}

/* -----------------------------------------------------
 * Helper: Get first two lines of article
 * ----------------------------------------------------- */
export function getFirstTwoLines(
  articleBody: ProcessedArticleBody | null,
): string {
  if (!articleBody) return "";

  const paragraphLines = articleBody
    .filter((el) => el.type === "paragraph")
    .flatMap((para) => {
      para.value ? para.value.split("\n") : "";
    });

  return paragraphLines.slice(0, 2).join("\n");
}
